import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { AccountingStandard } from '../models/accountingStandard';
import { AuditEmphasisOfMatter } from '../models/auditEmphasisOfMatter';
import { AuditFeeCategory } from '../models/auditFeeCategory';
import { AuditFirm } from '../models/auditFirm';
import { AuditGoingConcern } from '../models/auditGoingConcern';
import { AuditKeyMatter } from '../models/auditKeyMatter';
import { AuditMateriality } from '../models/auditMateriality';
import { AuditOpinion } from '../models/auditOpinion';
import { AuditOpinionModified } from '../models/auditOpinionModified';
import { AuditOpinionQualifiedOption } from '../models/auditOpinionQualifiedOption';
import { AuditSectionConfiguration, CreateAuditSectionConfigurationRequest, UpdateAuditSectionConfigurationRequest } from '../models/auditSectionConfiguration';
import { BoardMemberOccupation } from '../models/boardMemberOccupation';
import { BulkMergeRequest } from '../models/bulkMergeRequest';
import { CeoTurnoverReason } from '../models/ceoTurnoverReason';
import { CompanyDeregistrationReason } from '../models/companyDeregistrationReason';
import { CompensationConsultantFirm } from '../models/consultantFirm';
import { CompensationMultiplier } from '../models/compensationMultiplier';
import { CompensationStandard } from '../models/compensationStandard';
import { CompensationOccupation } from '../models/compensationOccupation';
import { CompensationCategoryConfiguration, CreateCompensationCategoryConfigurationRequest, UpdateCompensationCategoryConfigurationRequest } from '../models/compensationCategoryConfiguration';
import { Country, CountryFlat } from '../models/country';
import { CsrAssuranceProvider } from '../models/csrAssuranceProvider';
import {
  CsrCountryCapitalizationDto,
  UpsertCsrCountryCapitalizationsRequest
} from '../models/api/csrCountryCapitalizationDto';
import { CsrMeasurementUnit } from '../models/csrMeasurementUnit';
import { CsrStandard } from '../models/csrStandard';
import { Currency } from '../models/currency';
import { EducationField } from '../models/educationField';
import { FamilyCompanyStatus } from '../models/familyCompanyStatus';
import { FamilyGeneration } from '../models/familyGeneration';
import { FinancialExpertise } from '../models/financialExpertise';
import { HonorificTitle } from '../models/honorificTitle';
import { Index } from '../models/index';
import { Industry, IndustryFlat } from '../models/industry';
import { IndustryExpertise } from '../models/industryExpertise';
import { JobPosition } from '../models/jobPosition';
import { LargeShareholderCategory } from '../models/largeShareholderCategory';
import { Nationality } from '../models/nationality';
import { PoliticalPosition } from '../models/politicalPosition';
import { Profession } from '../models/profession';
import { Qualification } from '../models/qualification';
import { StockCategory } from '../models/stockCategory';
import { StockCoefficient } from '../models/stockCoefficient';
import { StockExchange } from '../models/stockExchange';
import { University } from '../models/university';
import { VoteResultsCategory } from '../models/voteResultsCategory';
import { VoteResultsOption } from '../models/voteResultsOption';
import { WorkExperienceCompany } from '../models/workExperienceCompany';
import { PaginatedResponse } from '../models/paginatedResponse';

@Injectable({
  providedIn: 'root'
})
export class LutService {
  private http = inject(HttpClient);
  private readonly API_BASE_URL = `${environment.apiUrl}/lut`;

  // ============================================
  // AccountingStandard
  // ============================================
  getAccountingStandards(): Observable<AccountingStandard[]> {
    return this.http.get<AccountingStandard[]>(`${this.API_BASE_URL}/accounting-standards`);
  }

  createAccountingStandard(data: Omit<AccountingStandard, 'id'>): Observable<AccountingStandard> {
    return this.http.post<AccountingStandard>(`${this.API_BASE_URL}/accounting-standards`, data);
  }

  getAccountingStandardById(id: string): Observable<AccountingStandard> {
    return this.http.get<AccountingStandard>(`${this.API_BASE_URL}/accounting-standards/${id}`);
  }

  updateAccountingStandard(id: string, data: AccountingStandard): Observable<AccountingStandard> {
    return this.http.put<AccountingStandard>(`${this.API_BASE_URL}/accounting-standards/${id}`, data);
  }

  deleteAccountingStandard(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/accounting-standards/${id}`);
  }

  mergeAccountingStandard(leftId: string, rightId: string): Observable<AccountingStandard> {
    return this.http.post<AccountingStandard>(`${this.API_BASE_URL}/accounting-standards/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeAccountingStandardsBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/accounting-standards/merge/bulk`, data);
  }

  getAccountingStandardReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/accounting-standards/${id}/references`);
  }

  // ============================================
  // AuditEmphasisOfMatters
  // ============================================
  getAuditEmphasisOfMatters(): Observable<AuditEmphasisOfMatter[]> {
    return this.http.get<AuditEmphasisOfMatter[]>(`${this.API_BASE_URL}/audit-emphasis-of-matters`);
  }

  createAuditEmphasisOfMatter(data: Omit<AuditEmphasisOfMatter, 'id'>): Observable<AuditEmphasisOfMatter> {
    return this.http.post<AuditEmphasisOfMatter>(`${this.API_BASE_URL}/audit-emphasis-of-matters`, data);
  }

  getAuditEmphasisOfMatterById(id: string): Observable<AuditEmphasisOfMatter> {
    return this.http.get<AuditEmphasisOfMatter>(`${this.API_BASE_URL}/audit-emphasis-of-matters/${id}`);
  }

  updateAuditEmphasisOfMatter(id: string, data: AuditEmphasisOfMatter): Observable<AuditEmphasisOfMatter> {
    return this.http.put<AuditEmphasisOfMatter>(`${this.API_BASE_URL}/audit-emphasis-of-matters/${id}`, data);
  }

  deleteAuditEmphasisOfMatter(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/audit-emphasis-of-matters/${id}`);
  }

  mergeAuditEmphasisOfMatter(leftId: string, rightId: string): Observable<AuditEmphasisOfMatter> {
    return this.http.post<AuditEmphasisOfMatter>(`${this.API_BASE_URL}/audit-emphasis-of-matters/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeAuditEmphasisOfMattersBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/audit-emphasis-of-matters/merge/bulk`, data);
  }

  getAuditEmphasisOfMatterReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/audit-emphasis-of-matters/${id}/references`);
  }

  // ============================================
  // AuditFeeCategory
  // ============================================
  getAuditFeeCategories(): Observable<AuditFeeCategory[]> {
    return this.http.get<AuditFeeCategory[]>(`${this.API_BASE_URL}/audit-fee-categories`);
  }

  createAuditFeeCategory(data: Omit<AuditFeeCategory, 'id'>): Observable<AuditFeeCategory> {
    return this.http.post<AuditFeeCategory>(`${this.API_BASE_URL}/audit-fee-categories`, data);
  }

  getAuditFeeCategoryById(id: string): Observable<AuditFeeCategory> {
    return this.http.get<AuditFeeCategory>(`${this.API_BASE_URL}/audit-fee-categories/${id}`);
  }

  updateAuditFeeCategory(id: string, data: AuditFeeCategory): Observable<AuditFeeCategory> {
    return this.http.put<AuditFeeCategory>(`${this.API_BASE_URL}/audit-fee-categories/${id}`, data);
  }

  deleteAuditFeeCategory(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/audit-fee-categories/${id}`);
  }

  mergeAuditFeeCategory(leftId: string, rightId: string): Observable<AuditFeeCategory> {
    return this.http.post<AuditFeeCategory>(`${this.API_BASE_URL}/audit-fee-categories/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeAuditFeeCategoriesBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/audit-fee-categories/merge/bulk`, data);
  }

  getAuditFeeCategoryReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/audit-fee-categories/${id}/references`);
  }

  // ============================================
  // AuditFirm
  // ============================================
  getAuditFirms(): Observable<AuditFirm[]> {
    return this.http.get<AuditFirm[]>(`${this.API_BASE_URL}/audit-firms`);
  }

  getAllAuditFirms(): Observable<AuditFirm[]> {
    return this.http.get<AuditFirm[]>(`${this.API_BASE_URL}/audit-firms/all`);
  }

  createAuditFirm(data: Omit<AuditFirm, 'id'>): Observable<AuditFirm> {
    return this.http.post<AuditFirm>(`${this.API_BASE_URL}/audit-firms`, data);
  }

  getAuditFirmById(id: string): Observable<AuditFirm> {
    return this.http.get<AuditFirm>(`${this.API_BASE_URL}/audit-firms/${id}`);
  }

  updateAuditFirm(id: string, data: AuditFirm): Observable<AuditFirm> {
    return this.http.put<AuditFirm>(`${this.API_BASE_URL}/audit-firms/${id}`, data);
  }

  deleteAuditFirm(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/audit-firms/${id}`);
  }

  mergeAuditFirm(leftId: string, rightId: string): Observable<AuditFirm> {
    return this.http.post<AuditFirm>(`${this.API_BASE_URL}/audit-firms/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeAuditFirmsBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/audit-firms/merge/bulk`, data);
  }

  getAuditFirmReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/audit-firms/${id}/references`);
  }

  getAuditFirmsPaginated(
    pageNumber: number = 1,
    pageSize: number = 50,
    sortBy: string = 'name',
    ascending: boolean = true
  ): Observable<PaginatedResponse<AuditFirm>> {
    const params = {
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
      sortBy,
      ascending: ascending.toString()
    };
    return this.http.get<PaginatedResponse<AuditFirm>>(
      `${this.API_BASE_URL}/audit-firms`,
      { params }
    );
  }

  searchAuditFirms(searchFilter: {
    pageNumber?: number;
    pageSize?: number;
    sortBy?: string;
    ascending?: boolean;
    name?: string;
  }): Observable<PaginatedResponse<AuditFirm>> {
    return this.http.post<PaginatedResponse<AuditFirm>>(
      `${this.API_BASE_URL}/audit-firms/search`,
      searchFilter
    );
  }

  // ============================================
  // AuditGoingConcern
  // ============================================
  getAuditGoingConcerns(): Observable<AuditGoingConcern[]> {
    return this.http.get<AuditGoingConcern[]>(`${this.API_BASE_URL}/audit-going-concerns`);
  }

  createAuditGoingConcern(data: Omit<AuditGoingConcern, 'id'>): Observable<AuditGoingConcern> {
    return this.http.post<AuditGoingConcern>(`${this.API_BASE_URL}/audit-going-concerns`, data);
  }

  getAuditGoingConcernById(id: string): Observable<AuditGoingConcern> {
    return this.http.get<AuditGoingConcern>(`${this.API_BASE_URL}/audit-going-concerns/${id}`);
  }

  updateAuditGoingConcern(id: string, data: AuditGoingConcern): Observable<AuditGoingConcern> {
    return this.http.put<AuditGoingConcern>(`${this.API_BASE_URL}/audit-going-concerns/${id}`, data);
  }

  deleteAuditGoingConcern(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/audit-going-concerns/${id}`);
  }

  mergeAuditGoingConcern(leftId: string, rightId: string): Observable<AuditGoingConcern> {
    return this.http.post<AuditGoingConcern>(`${this.API_BASE_URL}/audit-going-concerns/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeAuditGoingConcernsBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/audit-going-concerns/merge/bulk`, data);
  }

  getAuditGoingConcernReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/audit-going-concerns/${id}/references`);
  }

  // ============================================
  // AuditKeyMatter
  // ============================================
  getAuditKeyMatters(): Observable<AuditKeyMatter[]> {
    return this.http.get<AuditKeyMatter[]>(`${this.API_BASE_URL}/audit-key-matters`);
  }

  createAuditKeyMatter(data: Omit<AuditKeyMatter, 'id'>): Observable<AuditKeyMatter> {
    return this.http.post<AuditKeyMatter>(`${this.API_BASE_URL}/audit-key-matters`, data);
  }

  getAuditKeyMatterById(id: string): Observable<AuditKeyMatter> {
    return this.http.get<AuditKeyMatter>(`${this.API_BASE_URL}/audit-key-matters/${id}`);
  }

  updateAuditKeyMatter(id: string, data: AuditKeyMatter): Observable<AuditKeyMatter> {
    return this.http.put<AuditKeyMatter>(`${this.API_BASE_URL}/audit-key-matters/${id}`, data);
  }

  deleteAuditKeyMatter(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/audit-key-matters/${id}`);
  }

  mergeAuditKeyMatter(leftId: string, rightId: string): Observable<AuditKeyMatter> {
    return this.http.post<AuditKeyMatter>(`${this.API_BASE_URL}/audit-key-matters/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeAuditKeyMattersBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/audit-key-matters/merge/bulk`, data);
  }

  getAuditKeyMatterReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/audit-key-matters/${id}/references`);
  }

  getAuditKeyMattersPaginated(
    pageNumber: number = 1,
    pageSize: number = 50,
    sortBy: string = 'name',
    ascending: boolean = true
  ): Observable<PaginatedResponse<AuditKeyMatter>> {
    const params = {
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
      sortBy,
      ascending: ascending.toString()
    };
    return this.http.get<PaginatedResponse<AuditKeyMatter>>(`${this.API_BASE_URL}/audit-key-matters`, { params });
  }

  searchAuditKeyMatters(searchFilter: {
    pageNumber?: number;
    pageSize?: number;
    sortBy?: string;
    ascending?: boolean;
    name?: string;
    exactMatch?: boolean;
  }): Observable<PaginatedResponse<AuditKeyMatter>> {
    return this.http.post<PaginatedResponse<AuditKeyMatter>>(
      `${this.API_BASE_URL}/audit-key-matters/search`,
      searchFilter
    );
  }

  // ============================================
  // AuditMateriality
  // ============================================
  getAuditMaterialities(): Observable<AuditMateriality[]> {
    return this.http.get<AuditMateriality[]>(`${this.API_BASE_URL}/audit-materialities`);
  }

  createAuditMateriality(data: Omit<AuditMateriality, 'id'>): Observable<AuditMateriality> {
    return this.http.post<AuditMateriality>(`${this.API_BASE_URL}/audit-materialities`, data);
  }

  getAuditMaterialityById(id: string): Observable<AuditMateriality> {
    return this.http.get<AuditMateriality>(`${this.API_BASE_URL}/audit-materialities/${id}`);
  }

  updateAuditMateriality(id: string, data: AuditMateriality): Observable<AuditMateriality> {
    return this.http.put<AuditMateriality>(`${this.API_BASE_URL}/audit-materialities/${id}`, data);
  }

  deleteAuditMateriality(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/audit-materialities/${id}`);
  }

  mergeAuditMateriality(leftId: string, rightId: string): Observable<AuditMateriality> {
    return this.http.post<AuditMateriality>(`${this.API_BASE_URL}/audit-materialities/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeAuditMaterialitiesBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/audit-materialities/merge/bulk`, data);
  }

  getAuditMaterialityReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/audit-materialities/${id}/references`);
  }

  // ============================================
  // AuditOpinion
  // ============================================
  getAuditOpinions(): Observable<AuditOpinion[]> {
    return this.http.get<AuditOpinion[]>(`${this.API_BASE_URL}/audit-opinions`);
  }

  createAuditOpinion(data: Omit<AuditOpinion, 'id'>): Observable<AuditOpinion> {
    return this.http.post<AuditOpinion>(`${this.API_BASE_URL}/audit-opinions`, data);
  }

  getAuditOpinionById(id: string): Observable<AuditOpinion> {
    return this.http.get<AuditOpinion>(`${this.API_BASE_URL}/audit-opinions/${id}`);
  }

  updateAuditOpinion(id: string, data: AuditOpinion): Observable<AuditOpinion> {
    return this.http.put<AuditOpinion>(`${this.API_BASE_URL}/audit-opinions/${id}`, data);
  }

  deleteAuditOpinion(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/audit-opinions/${id}`);
  }

  mergeAuditOpinion(leftId: string, rightId: string): Observable<AuditOpinion> {
    return this.http.post<AuditOpinion>(`${this.API_BASE_URL}/audit-opinions/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeAuditOpinionsBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/audit-opinions/merge/bulk`, data);
  }

  getAuditOpinionReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/audit-opinions/${id}/references`);
  }

  // ============================================
  // AuditOpinionModified
  // ============================================
  getAuditOpinionModifieds(): Observable<AuditOpinionModified[]> {
    return this.http.get<AuditOpinionModified[]>(`${this.API_BASE_URL}/audit-opinion-modifieds`);
  }

  createAuditOpinionModified(data: Omit<AuditOpinionModified, 'id'>): Observable<AuditOpinionModified> {
    return this.http.post<AuditOpinionModified>(`${this.API_BASE_URL}/audit-opinion-modifieds`, data);
  }

  getAuditOpinionModifiedById(id: string): Observable<AuditOpinionModified> {
    return this.http.get<AuditOpinionModified>(`${this.API_BASE_URL}/audit-opinion-modifieds/${id}`);
  }

  updateAuditOpinionModified(id: string, data: AuditOpinionModified): Observable<AuditOpinionModified> {
    return this.http.put<AuditOpinionModified>(`${this.API_BASE_URL}/audit-opinion-modifieds/${id}`, data);
  }

  deleteAuditOpinionModified(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/audit-opinion-modifieds/${id}`);
  }

  mergeAuditOpinionModified(leftId: string, rightId: string): Observable<AuditOpinionModified> {
    return this.http.post<AuditOpinionModified>(`${this.API_BASE_URL}/audit-opinion-modifieds/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeAuditOpinionModifiedsBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/audit-opinion-modifieds/merge/bulk`, data);
  }

  getAuditOpinionModifiedReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/audit-opinion-modifieds/${id}/references`);
  }

  // ============================================
  // AuditOpinionQualifiedOption
  // ============================================
  getAuditOpinionQualifiedOptions(): Observable<AuditOpinionQualifiedOption[]> {
    return this.http.get<AuditOpinionQualifiedOption[]>(`${this.API_BASE_URL}/audit-opinion-qualified-options`);
  }

  createAuditOpinionQualifiedOption(data: Omit<AuditOpinionQualifiedOption, 'id'>): Observable<AuditOpinionQualifiedOption> {
    return this.http.post<AuditOpinionQualifiedOption>(`${this.API_BASE_URL}/audit-opinion-qualified-options`, data);
  }

  getAuditOpinionQualifiedOptionById(id: string): Observable<AuditOpinionQualifiedOption> {
    return this.http.get<AuditOpinionQualifiedOption>(`${this.API_BASE_URL}/audit-opinion-qualified-options/${id}`);
  }

  updateAuditOpinionQualifiedOption(id: string, data: AuditOpinionQualifiedOption): Observable<AuditOpinionQualifiedOption> {
    return this.http.put<AuditOpinionQualifiedOption>(`${this.API_BASE_URL}/audit-opinion-qualified-options/${id}`, data);
  }

  deleteAuditOpinionQualifiedOption(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/audit-opinion-qualified-options/${id}`);
  }

  mergeAuditOpinionQualifiedOption(leftId: string, rightId: string): Observable<AuditOpinionQualifiedOption> {
    return this.http.post<AuditOpinionQualifiedOption>(`${this.API_BASE_URL}/audit-opinion-qualified-options/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeAuditOpinionQualifiedOptionsBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/audit-opinion-qualified-options/merge/bulk`, data);
  }

  getAuditOpinionQualifiedOptionReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/audit-opinion-qualified-options/${id}/references`);
  }

  // ============================================
  // AuditSectionConfiguration
  // ============================================
  getAuditSectionConfigurations(): Observable<AuditSectionConfiguration[]> {
    return this.http.get<AuditSectionConfiguration[]>(`${environment.apiUrl}/audit-section-configurations`);
  }

  createAuditSectionConfiguration(data: CreateAuditSectionConfigurationRequest): Observable<AuditSectionConfiguration> {
    return this.http.post<AuditSectionConfiguration>(`${environment.apiUrl}/audit-section-configurations`, data);
  }

  getAuditSectionConfigurationById(id: string): Observable<AuditSectionConfiguration> {
    return this.http.get<AuditSectionConfiguration>(`${environment.apiUrl}/audit-section-configurations/${id}`);
  }

  updateAuditSectionConfiguration(id: string, data: UpdateAuditSectionConfigurationRequest): Observable<AuditSectionConfiguration> {
    return this.http.put<AuditSectionConfiguration>(`${environment.apiUrl}/audit-section-configurations/${id}`, data);
  }

  deleteAuditSectionConfiguration(id: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/audit-section-configurations/${id}`);
  }

  getAuditSectionConfigurationReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${environment.apiUrl}/audit-section-configurations/${id}/references`);
  }

  // ============================================
  // BoardMemberOccupation
  // ============================================
  getBoardMemberOccupations(): Observable<BoardMemberOccupation[]> {
    return this.http.get<BoardMemberOccupation[]>(`${this.API_BASE_URL}/board-member-occupations`);
  }

  createBoardMemberOccupation(data: Omit<BoardMemberOccupation, 'id'>): Observable<BoardMemberOccupation> {
    return this.http.post<BoardMemberOccupation>(`${this.API_BASE_URL}/board-member-occupations`, data);
  }

  getBoardMemberOccupationById(id: string): Observable<BoardMemberOccupation> {
    return this.http.get<BoardMemberOccupation>(`${this.API_BASE_URL}/board-member-occupations/${id}`);
  }

  updateBoardMemberOccupation(id: string, data: BoardMemberOccupation): Observable<BoardMemberOccupation> {
    return this.http.put<BoardMemberOccupation>(`${this.API_BASE_URL}/board-member-occupations/${id}`, data);
  }

  deleteBoardMemberOccupation(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/board-member-occupations/${id}`);
  }

  mergeBoardMemberOccupation(leftId: string, rightId: string): Observable<BoardMemberOccupation> {
    return this.http.post<BoardMemberOccupation>(`${this.API_BASE_URL}/board-member-occupations/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeBoardMemberOccupationsBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/board-member-occupations/merge/bulk`, data);
  }

  getBoardMemberOccupationReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/board-member-occupations/${id}/references`);
  }

  // ============================================
  // CeoTurnoverReason
  // ============================================
  getCeoTurnoverReasons(): Observable<CeoTurnoverReason[]> {
    return this.http.get<CeoTurnoverReason[]>(`${this.API_BASE_URL}/ceo-turnover-reasons`);
  }

  createCeoTurnoverReason(data: Omit<CeoTurnoverReason, 'id'>): Observable<CeoTurnoverReason> {
    return this.http.post<CeoTurnoverReason>(`${this.API_BASE_URL}/ceo-turnover-reasons`, data);
  }

  getCeoTurnoverReasonById(id: string): Observable<CeoTurnoverReason> {
    return this.http.get<CeoTurnoverReason>(`${this.API_BASE_URL}/ceo-turnover-reasons/${id}`);
  }

  updateCeoTurnoverReason(id: string, data: CeoTurnoverReason): Observable<CeoTurnoverReason> {
    return this.http.put<CeoTurnoverReason>(`${this.API_BASE_URL}/ceo-turnover-reasons/${id}`, data);
  }

  deleteCeoTurnoverReason(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/ceo-turnover-reasons/${id}`);
  }

  mergeCeoTurnoverReason(leftId: string, rightId: string): Observable<CeoTurnoverReason> {
    return this.http.post<CeoTurnoverReason>(`${this.API_BASE_URL}/ceo-turnover-reasons/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeCeoTurnoverReasonsBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/ceo-turnover-reasons/merge/bulk`, data);
  }

  getCeoTurnoverReasonReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/ceo-turnover-reasons/${id}/references`);
  }

  // ============================================
  // CompanyDeregistrationReason
  // ============================================
  getCompanyDeregistrationReasons(): Observable<CompanyDeregistrationReason[]> {
    return this.http.get<CompanyDeregistrationReason[]>(`${this.API_BASE_URL}/company-deregistration-reasons`);
  }

  createCompanyDeregistrationReason(data: Omit<CompanyDeregistrationReason, 'id'>): Observable<CompanyDeregistrationReason> {
    return this.http.post<CompanyDeregistrationReason>(`${this.API_BASE_URL}/company-deregistration-reasons`, data);
  }

  getCompanyDeregistrationReasonById(id: string): Observable<CompanyDeregistrationReason> {
    return this.http.get<CompanyDeregistrationReason>(`${this.API_BASE_URL}/company-deregistration-reasons/${id}`);
  }

  updateCompanyDeregistrationReason(id: string, data: CompanyDeregistrationReason): Observable<CompanyDeregistrationReason> {
    return this.http.put<CompanyDeregistrationReason>(`${this.API_BASE_URL}/company-deregistration-reasons/${id}`, data);
  }

  deleteCompanyDeregistrationReason(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/company-deregistration-reasons/${id}`);
  }

  mergeCompanyDeregistrationReason(leftId: string, rightId: string): Observable<CompanyDeregistrationReason> {
    return this.http.post<CompanyDeregistrationReason>(`${this.API_BASE_URL}/company-deregistration-reasons/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeCompanyDeregistrationReasonsBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/company-deregistration-reasons/merge/bulk`, data);
  }

  getCompanyDeregistrationReasonReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/company-deregistration-reasons/${id}/references`);
  }

  // ============================================
  // CompensationConsultantFirm
  // ============================================
  getConsultantFirms(): Observable<CompensationConsultantFirm[]> {
    return this.http.get<CompensationConsultantFirm[]>(`${this.API_BASE_URL}/compensation-consultant-firms/all`);
  }

  getConsultantFirmsPaginated(
    pageNumber: number = 1,
    pageSize: number = 50,
    sortBy: string = 'name',
    ascending: boolean = true
  ): Observable<PaginatedResponse<CompensationConsultantFirm>> {
    const params = {
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
      sortBy,
      ascending: ascending.toString()
    };
    return this.http.get<PaginatedResponse<CompensationConsultantFirm>>(
      `${this.API_BASE_URL}/compensation-consultant-firms`,
      { params }
    );
  }

  searchConsultantFirms(searchFilter: {
    pageNumber?: number;
    pageSize?: number;
    sortBy?: string;
    ascending?: boolean;
    name?: string;
    exactMatch?: boolean;
  }): Observable<PaginatedResponse<CompensationConsultantFirm>> {
    return this.http.post<PaginatedResponse<CompensationConsultantFirm>>(
      `${this.API_BASE_URL}/compensation-consultant-firms/search`,
      searchFilter
    );
  }

  createConsultantFirm(data: Omit<CompensationConsultantFirm, 'id'>): Observable<CompensationConsultantFirm> {
    return this.http.post<CompensationConsultantFirm>(`${this.API_BASE_URL}/compensation-consultant-firms`, data);
  }

  getConsultantFirmById(id: string): Observable<CompensationConsultantFirm> {
    return this.http.get<CompensationConsultantFirm>(`${this.API_BASE_URL}/compensation-consultant-firms/${id}`);
  }

  updateConsultantFirm(id: string, data: CompensationConsultantFirm): Observable<CompensationConsultantFirm> {
    return this.http.put<CompensationConsultantFirm>(`${this.API_BASE_URL}/compensation-consultant-firms/${id}`, data);
  }

  deleteConsultantFirm(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/compensation-consultant-firms/${id}`);
  }

  mergeConsultantFirm(leftId: string, rightId: string): Observable<CompensationConsultantFirm> {
    return this.http.post<CompensationConsultantFirm>(`${this.API_BASE_URL}/compensation-consultant-firms/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeConsultantFirmsBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/compensation-consultant-firms/merge/bulk`, data);
  }

  getConsultantFirmReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/compensation-consultant-firms/${id}/references`);
  }

  // ============================================
  // CompensationMultiplier
  // ============================================
  getCompensationMultipliers(): Observable<CompensationMultiplier[]> {
    return this.http.get<CompensationMultiplier[]>(`${this.API_BASE_URL}/compensation-multipliers`);
  }

  createCompensationMultiplier(data: Omit<CompensationMultiplier, 'id'>): Observable<CompensationMultiplier> {
    return this.http.post<CompensationMultiplier>(`${this.API_BASE_URL}/compensation-multipliers`, data);
  }

  getCompensationMultiplierById(id: string): Observable<CompensationMultiplier> {
    return this.http.get<CompensationMultiplier>(`${this.API_BASE_URL}/compensation-multipliers/${id}`);
  }

  updateCompensationMultiplier(id: string, data: CompensationMultiplier): Observable<CompensationMultiplier> {
    return this.http.put<CompensationMultiplier>(`${this.API_BASE_URL}/compensation-multipliers/${id}`, data);
  }

  deleteCompensationMultiplier(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/compensation-multipliers/${id}`);
  }

  mergeCompensationMultiplier(leftId: string, rightId: string): Observable<CompensationMultiplier> {
    return this.http.post<CompensationMultiplier>(`${this.API_BASE_URL}/compensation-multipliers/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeCompensationMultipliersBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/compensation-multipliers/merge/bulk`, data);
  }

  getCompensationMultiplierReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/compensation-multipliers/${id}/references`);
  }

  // ============================================
  // CompensationOccupation
  // ============================================
  getCompensationOccupations(): Observable<CompensationOccupation[]> {
    return this.http.get<CompensationOccupation[]>(`${this.API_BASE_URL}/compensation-occupations`);
  }

  createCompensationOccupation(data: Omit<CompensationOccupation, 'id'>): Observable<CompensationOccupation> {
    return this.http.post<CompensationOccupation>(`${this.API_BASE_URL}/compensation-occupations`, data);
  }

  getCompensationOccupationById(id: string): Observable<CompensationOccupation> {
    return this.http.get<CompensationOccupation>(`${this.API_BASE_URL}/compensation-occupations/${id}`);
  }

  updateCompensationOccupation(id: string, data: CompensationOccupation): Observable<CompensationOccupation> {
    return this.http.put<CompensationOccupation>(`${this.API_BASE_URL}/compensation-occupations/${id}`, data);
  }

  deleteCompensationOccupation(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/compensation-occupations/${id}`);
  }

  mergeCompensationOccupation(leftId: string, rightId: string): Observable<CompensationOccupation> {
    return this.http.post<CompensationOccupation>(`${this.API_BASE_URL}/compensation-occupations/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeCompensationOccupationsBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/compensation-occupations/merge/bulk`, data);
  }

  getCompensationOccupationReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/compensation-occupations/${id}/references`);
  }

  // ============================================
  // CompensationStandard
  // ============================================
  getCompensationStandards(): Observable<CompensationStandard[]> {
    return this.http.get<CompensationStandard[]>(`${this.API_BASE_URL}/compensation-standards`);
  }

  createCompensationStandard(data: Omit<CompensationStandard, 'id'>): Observable<CompensationStandard> {
    return this.http.post<CompensationStandard>(`${this.API_BASE_URL}/compensation-standards`, data);
  }

  getCompensationStandardById(id: string): Observable<CompensationStandard> {
    return this.http.get<CompensationStandard>(`${this.API_BASE_URL}/compensation-standards/${id}`);
  }

  updateCompensationStandard(id: string, data: CompensationStandard): Observable<CompensationStandard> {
    return this.http.put<CompensationStandard>(`${this.API_BASE_URL}/compensation-standards/${id}`, data);
  }

  deleteCompensationStandard(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/compensation-standards/${id}`);
  }

  mergeCompensationStandard(leftId: string, rightId: string): Observable<CompensationStandard> {
    return this.http.post<CompensationStandard>(`${this.API_BASE_URL}/compensation-standards/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeCompensationStandardsBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/compensation-standards/merge/bulk`, data);
  }

  getCompensationStandardReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/compensation-standards/${id}/references`);
  }

  // ============================================
  // CompensationCategoryConfiguration
  // ============================================
  getCompensationCategoryConfigurations(): Observable<CompensationCategoryConfiguration[]> {
    return this.http.get<CompensationCategoryConfiguration[]>(`${environment.apiUrl}/compensation-category-configurations`);
  }

  createCompensationCategoryConfiguration(data: CreateCompensationCategoryConfigurationRequest): Observable<CompensationCategoryConfiguration> {
    return this.http.post<CompensationCategoryConfiguration>(`${environment.apiUrl}/compensation-category-configurations`, data);
  }

  getCompensationCategoryConfigurationById(id: string): Observable<CompensationCategoryConfiguration> {
    return this.http.get<CompensationCategoryConfiguration>(`${environment.apiUrl}/compensation-category-configurations/${id}`);
  }

  updateCompensationCategoryConfiguration(id: string, data: UpdateCompensationCategoryConfigurationRequest): Observable<CompensationCategoryConfiguration> {
    return this.http.put<CompensationCategoryConfiguration>(`${environment.apiUrl}/compensation-category-configurations/${id}`, data);
  }

  deleteCompensationCategoryConfiguration(id: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/compensation-category-configurations/${id}`);
  }

  getCompensationCategoryConfigurationReferences(id: string): Observable<string> {
    return this.http.get<string>(`${environment.apiUrl}/compensation-category-configurations/${id}/references`);
  }

  // ============================================
  // Country
  // ============================================
  getCountries(): Observable<CountryFlat[]> {
    return this.http.get<CountryFlat[]>(`${this.API_BASE_URL}/countries`);
  }

  getVisibleCountries(): Observable<CountryFlat[]> {
    return this.getCountries().pipe(
      map(countries => countries.filter(c => c.isVisible))
    );
  }

  createCountry(data: Omit<CountryFlat, 'id'>): Observable<CountryFlat> {
    return this.http.post<CountryFlat>(`${this.API_BASE_URL}/countries`, data);
  }

  getCountryById(id: string): Observable<CountryFlat> {
    return this.http.get<CountryFlat>(`${this.API_BASE_URL}/countries/${id}`);
  }

  updateCountry(id: string, data: CountryFlat): Observable<CountryFlat> {
    return this.http.put<CountryFlat>(`${this.API_BASE_URL}/countries/${id}`, data);
  }

  deleteCountry(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/countries/${id}`);
  }

  mergeCountry(leftId: string, rightId: string): Observable<CountryFlat> {
    return this.http.post<CountryFlat>(`${this.API_BASE_URL}/countries/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeCountriesBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/countries/merge/bulk`, data);
  }

  getCountryReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/countries/${id}/references`);
  }

  // ============================================
  // CsrAssuranceProvider
  // ============================================
  getCsrAssuranceProviders(): Observable<CsrAssuranceProvider[]> {
    return this.http.get<CsrAssuranceProvider[]>(`${this.API_BASE_URL}/csr-assurance-providers`);
  }

  createCsrAssuranceProvider(data: Omit<CsrAssuranceProvider, 'id'>): Observable<CsrAssuranceProvider> {
    return this.http.post<CsrAssuranceProvider>(`${this.API_BASE_URL}/csr-assurance-providers`, data);
  }

  getCsrAssuranceProviderById(id: string): Observable<CsrAssuranceProvider> {
    return this.http.get<CsrAssuranceProvider>(`${this.API_BASE_URL}/csr-assurance-providers/${id}`);
  }

  updateCsrAssuranceProvider(id: string, data: CsrAssuranceProvider): Observable<CsrAssuranceProvider> {
    return this.http.put<CsrAssuranceProvider>(`${this.API_BASE_URL}/csr-assurance-providers/${id}`, data);
  }

  deleteCsrAssuranceProvider(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/csr-assurance-providers/${id}`);
  }

  mergeCsrAssuranceProvider(leftId: string, rightId: string): Observable<CsrAssuranceProvider> {
    return this.http.post<CsrAssuranceProvider>(`${this.API_BASE_URL}/csr-assurance-providers/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeCsrAssuranceProvidersBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/csr-assurance-providers/merge/bulk`, data);
  }

  getCsrAssuranceProviderReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/csr-assurance-providers/${id}/references`);
  }

  // ============================================
  // CsrMeasurementUnit
  // ============================================
  getCsrMeasurementUnits(): Observable<CsrMeasurementUnit[]> {
    return this.http.get<CsrMeasurementUnit[]>(`${this.API_BASE_URL}/csr-measurement-units`);
  }

  createCsrMeasurementUnit(data: Omit<CsrMeasurementUnit, 'id'>): Observable<CsrMeasurementUnit> {
    return this.http.post<CsrMeasurementUnit>(`${this.API_BASE_URL}/csr-measurement-units`, data);
  }

  getCsrMeasurementUnitById(id: string): Observable<CsrMeasurementUnit> {
    return this.http.get<CsrMeasurementUnit>(`${this.API_BASE_URL}/csr-measurement-units/${id}`);
  }

  updateCsrMeasurementUnit(id: string, data: CsrMeasurementUnit): Observable<CsrMeasurementUnit> {
    return this.http.put<CsrMeasurementUnit>(`${this.API_BASE_URL}/csr-measurement-units/${id}`, data);
  }

  deleteCsrMeasurementUnit(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/csr-measurement-units/${id}`);
  }

  mergeCsrMeasurementUnit(leftId: string, rightId: string): Observable<CsrMeasurementUnit> {
    return this.http.post<CsrMeasurementUnit>(`${this.API_BASE_URL}/csr-measurement-units/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeCsrMeasurementUnitsBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/csr-measurement-units/merge/bulk`, data);
  }

  getCsrMeasurementUnitReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/csr-measurement-units/${id}/references`);
  }

  // ============================================
  // CsrStandard
  // ============================================
  getCsrStandards(): Observable<CsrStandard[]> {
    return this.http.get<CsrStandard[]>(`${this.API_BASE_URL}/csr-standards`);
  }

  createCsrStandard(data: Omit<CsrStandard, 'id'>): Observable<CsrStandard> {
    return this.http.post<CsrStandard>(`${this.API_BASE_URL}/csr-standards`, data);
  }

  getCsrStandardById(id: string): Observable<CsrStandard> {
    return this.http.get<CsrStandard>(`${this.API_BASE_URL}/csr-standards/${id}`);
  }

  updateCsrStandard(id: string, data: CsrStandard): Observable<CsrStandard> {
    return this.http.put<CsrStandard>(`${this.API_BASE_URL}/csr-standards/${id}`, data);
  }

  deleteCsrStandard(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/csr-standards/${id}`);
  }

  mergeCsrStandard(leftId: string, rightId: string): Observable<CsrStandard> {
    return this.http.post<CsrStandard>(`${this.API_BASE_URL}/csr-standards/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeCsrStandardsBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/csr-standards/merge/bulk`, data);
  }

  getCsrStandardReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/csr-standards/${id}/references`);
  }

  // ============================================
  // CsrCountryCapitalization
  // ============================================
  getCsrCountryCapitalizations(year: number): Observable<CsrCountryCapitalizationDto> {
    const params = new HttpParams().set('year', year.toString());
    return this.http.get<CsrCountryCapitalizationDto>(`${this.API_BASE_URL}/csr-country-capitalizations`, { params });
  }

  upsertCsrCountryCapitalizations(data: UpsertCsrCountryCapitalizationsRequest): Observable<CsrCountryCapitalizationDto> {
    return this.http.put<CsrCountryCapitalizationDto>(`${this.API_BASE_URL}/csr-country-capitalizations`, data);
  }

  deleteCsrCountryCapitalizations(year: number): Observable<void> {
    const params = new HttpParams().set('year', year.toString());
    return this.http.delete<void>(`${this.API_BASE_URL}/csr-country-capitalizations`, { params });
  }

  // ============================================
  // Currency
  // ============================================
  getCurrencies(): Observable<Currency[]> {
    return this.http.get<Currency[]>(`${this.API_BASE_URL}/currencies`);
  }

  createCurrency(data: Omit<Currency, 'id'>): Observable<Currency> {
    return this.http.post<Currency>(`${this.API_BASE_URL}/currencies`, data);
  }

  getCurrencyById(id: string): Observable<Currency> {
    return this.http.get<Currency>(`${this.API_BASE_URL}/currencies/${id}`);
  }

  updateCurrency(id: string, data: Currency): Observable<Currency> {
    return this.http.put<Currency>(`${this.API_BASE_URL}/currencies/${id}`, data);
  }

  deleteCurrency(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/currencies/${id}`);
  }

  mergeCurrency(leftId: string, rightId: string): Observable<Currency> {
    return this.http.post<Currency>(`${this.API_BASE_URL}/currencies/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeCurrenciesBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/currencies/merge/bulk`, data);
  }

  getCurrencyReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/currencies/${id}/references`);
  }

  // ============================================
  // EducationField
  // ============================================
  getEducationFields(): Observable<EducationField[]> {
    return this.http.get<EducationField[]>(`${this.API_BASE_URL}/education-fields`);
  }

  getEducationFieldsPaginated(
    pageNumber: number = 1,
    pageSize: number = 50,
    sortBy: string = 'name',
    ascending: boolean = true
  ): Observable<PaginatedResponse<EducationField>> {
    const params = {
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
      sortBy,
      ascending: ascending.toString()
    };
    return this.http.get<PaginatedResponse<EducationField>>(
      `${this.API_BASE_URL}/education-fields`,
      { params }
    );
  }

  searchEducationFields(searchFilter: {
    pageNumber?: number;
    pageSize?: number;
    sortBy?: string;
    ascending?: boolean;
    name?: string;
    exactMatch?: boolean;
  }): Observable<PaginatedResponse<EducationField>> {
    return this.http.post<PaginatedResponse<EducationField>>(
      `${this.API_BASE_URL}/education-fields/search`,
      searchFilter
    );
  }

  createEducationField(data: Omit<EducationField, 'id'>): Observable<EducationField> {
    return this.http.post<EducationField>(`${this.API_BASE_URL}/education-fields`, data);
  }

  getEducationFieldById(id: string): Observable<EducationField> {
    return this.http.get<EducationField>(`${this.API_BASE_URL}/education-fields/${id}`);
  }

  updateEducationField(id: string, data: EducationField): Observable<EducationField> {
    return this.http.put<EducationField>(`${this.API_BASE_URL}/education-fields/${id}`, data);
  }

  deleteEducationField(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/education-fields/${id}`);
  }

  mergeEducationField(leftId: string, rightId: string): Observable<EducationField> {
    return this.http.post<EducationField>(`${this.API_BASE_URL}/education-fields/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeEducationFieldsBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/education-fields/merge/bulk`, data);
  }

  getEducationFieldReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/education-fields/${id}/references`);
  }

  exportEducationFieldsToCsv(filter: { name?: string; exactMatch?: boolean } = {}): Observable<Blob> {
    return this.http.post(`${this.API_BASE_URL}/education-fields/export`, filter, { responseType: 'blob' });
  }

  // ============================================
  // FamilyCompanyStatus
  // ============================================
  getFamilyCompanyStatuses(): Observable<FamilyCompanyStatus[]> {
    return this.http.get<FamilyCompanyStatus[]>(`${this.API_BASE_URL}/family-company-statuses`);
  }

  createFamilyCompanyStatus(data: Omit<FamilyCompanyStatus, 'id'>): Observable<FamilyCompanyStatus> {
    return this.http.post<FamilyCompanyStatus>(`${this.API_BASE_URL}/family-company-statuses`, data);
  }

  getFamilyCompanyStatusById(id: string): Observable<FamilyCompanyStatus> {
    return this.http.get<FamilyCompanyStatus>(`${this.API_BASE_URL}/family-company-statuses/${id}`);
  }

  updateFamilyCompanyStatus(id: string, data: FamilyCompanyStatus): Observable<FamilyCompanyStatus> {
    return this.http.put<FamilyCompanyStatus>(`${this.API_BASE_URL}/family-company-statuses/${id}`, data);
  }

  deleteFamilyCompanyStatus(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/family-company-statuses/${id}`);
  }

  mergeFamilyCompanyStatus(leftId: string, rightId: string): Observable<FamilyCompanyStatus> {
    return this.http.post<FamilyCompanyStatus>(`${this.API_BASE_URL}/family-company-statuses/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeFamilyCompanyStatusesBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/family-company-statuses/merge/bulk`, data);
  }

  getFamilyCompanyStatusReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/family-company-statuses/${id}/references`);
  }

  // ============================================
  // FamilyGeneration
  // ============================================
  getFamilyGenerations(): Observable<FamilyGeneration[]> {
    return this.http.get<FamilyGeneration[]>(`${this.API_BASE_URL}/family-generations`);
  }

  createFamilyGeneration(data: Omit<FamilyGeneration, 'id'>): Observable<FamilyGeneration> {
    return this.http.post<FamilyGeneration>(`${this.API_BASE_URL}/family-generations`, data);
  }

  getFamilyGenerationById(id: string): Observable<FamilyGeneration> {
    return this.http.get<FamilyGeneration>(`${this.API_BASE_URL}/family-generations/${id}`);
  }

  updateFamilyGeneration(id: string, data: FamilyGeneration): Observable<FamilyGeneration> {
    return this.http.put<FamilyGeneration>(`${this.API_BASE_URL}/family-generations/${id}`, data);
  }

  deleteFamilyGeneration(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/family-generations/${id}`);
  }

  getFamilyGenerationReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/family-generations/${id}/references`);
  }

  // ============================================
  // FinancialExpertise
  // ============================================
  getFinancialExpertise(): Observable<FinancialExpertise[]> {
    return this.http.get<FinancialExpertise[]>(`${this.API_BASE_URL}/financial-expertise`);
  }

  createFinancialExpertise(data: Omit<FinancialExpertise, 'id'>): Observable<FinancialExpertise> {
    return this.http.post<FinancialExpertise>(`${this.API_BASE_URL}/financial-expertise`, data);
  }

  getFinancialExpertiseById(id: string): Observable<FinancialExpertise> {
    return this.http.get<FinancialExpertise>(`${this.API_BASE_URL}/financial-expertise/${id}`);
  }

  updateFinancialExpertise(id: string, data: FinancialExpertise): Observable<FinancialExpertise> {
    return this.http.put<FinancialExpertise>(`${this.API_BASE_URL}/financial-expertise/${id}`, data);
  }

  deleteFinancialExpertise(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/financial-expertise/${id}`);
  }

  mergeFinancialExpertise(leftId: string, rightId: string): Observable<FinancialExpertise> {
    return this.http.post<FinancialExpertise>(`${this.API_BASE_URL}/financial-expertise/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeFinancialExpertiseBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/financial-expertise/merge/bulk`, data);
  }

  getFinancialExpertiseReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/financial-expertise/${id}/references`);
  }

  // ============================================
  // HonorificTitle
  // ============================================
  getHonorificTitles(): Observable<HonorificTitle[]> {
    return this.http.get<HonorificTitle[]>(`${this.API_BASE_URL}/honorific-titles`);
  }

  createHonorificTitle(data: Omit<HonorificTitle, 'id'>): Observable<HonorificTitle> {
    return this.http.post<HonorificTitle>(`${this.API_BASE_URL}/honorific-titles`, data);
  }

  getHonorificTitleById(id: string): Observable<HonorificTitle> {
    return this.http.get<HonorificTitle>(`${this.API_BASE_URL}/honorific-titles/${id}`);
  }

  updateHonorificTitle(id: string, data: HonorificTitle): Observable<HonorificTitle> {
    return this.http.put<HonorificTitle>(`${this.API_BASE_URL}/honorific-titles/${id}`, data);
  }

  deleteHonorificTitle(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/honorific-titles/${id}`);
  }

  mergeHonorificTitle(leftId: string, rightId: string): Observable<HonorificTitle> {
    return this.http.post<HonorificTitle>(`${this.API_BASE_URL}/honorific-titles/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeHonorificTitlesBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/honorific-titles/merge/bulk`, data);
  }

  getHonorificTitleReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/honorific-titles/${id}/references`);
  }

  // ============================================
  // Industry
  // ============================================
  getIndustries(): Observable<IndustryFlat[]> {
    return this.http.get<IndustryFlat[]>(`${this.API_BASE_URL}/industries`);
  }

  createIndustry(data: Omit<IndustryFlat, 'id'>): Observable<IndustryFlat> {
    return this.http.post<IndustryFlat>(`${this.API_BASE_URL}/industries`, data);
  }

  getIndustryById(id: string): Observable<IndustryFlat> {
    return this.http.get<IndustryFlat>(`${this.API_BASE_URL}/industries/${id}`);
  }

  updateIndustry(id: string, data: IndustryFlat): Observable<IndustryFlat> {
    return this.http.put<IndustryFlat>(`${this.API_BASE_URL}/industries/${id}`, data);
  }

  deleteIndustry(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/industries/${id}`);
  }

  mergeIndustry(leftId: string, rightId: string): Observable<IndustryFlat> {
    return this.http.post<IndustryFlat>(`${this.API_BASE_URL}/industries/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeIndustriesBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/industries/merge/bulk`, data);
  }

  getIndustryReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/industries/${id}/references`);
  }

  // ============================================
  // IndustryExpertise
  // ============================================
  getIndustryExpertise(): Observable<IndustryExpertise[]> {
    return this.http.get<IndustryExpertise[]>(`${this.API_BASE_URL}/industry-expertise`);
  }

  createIndustryExpertise(data: Omit<IndustryExpertise, 'id'>): Observable<IndustryExpertise> {
    return this.http.post<IndustryExpertise>(`${this.API_BASE_URL}/industry-expertise`, data);
  }

  getIndustryExpertiseById(id: string): Observable<IndustryExpertise> {
    return this.http.get<IndustryExpertise>(`${this.API_BASE_URL}/industry-expertise/${id}`);
  }

  updateIndustryExpertise(id: string, data: IndustryExpertise): Observable<IndustryExpertise> {
    return this.http.put<IndustryExpertise>(`${this.API_BASE_URL}/industry-expertise/${id}`, data);
  }

  deleteIndustryExpertise(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/industry-expertise/${id}`);
  }

  mergeIndustryExpertise(leftId: string, rightId: string): Observable<IndustryExpertise> {
    return this.http.post<IndustryExpertise>(`${this.API_BASE_URL}/industry-expertise/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeIndustryExpertiseBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/industry-expertise/merge/bulk`, data);
  }

  getIndustryExpertiseReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/industry-expertise/${id}/references`);
  }

  // ============================================
  // JobPosition
  // ============================================
  getJobPositions(): Observable<JobPosition[]> {
    return this.http.get<JobPosition[]>(`${this.API_BASE_URL}/job-positions`);
  }

  createJobPosition(data: Omit<JobPosition, 'id'>): Observable<JobPosition> {
    return this.http.post<JobPosition>(`${this.API_BASE_URL}/job-positions`, data);
  }

  getJobPositionById(id: string): Observable<JobPosition> {
    return this.http.get<JobPosition>(`${this.API_BASE_URL}/job-positions/${id}`);
  }

  updateJobPosition(id: string, data: JobPosition): Observable<JobPosition> {
    return this.http.put<JobPosition>(`${this.API_BASE_URL}/job-positions/${id}`, data);
  }

  deleteJobPosition(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/job-positions/${id}`);
  }

  mergeJobPosition(leftId: string, rightId: string): Observable<JobPosition> {
    return this.http.post<JobPosition>(`${this.API_BASE_URL}/job-positions/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeJobPositionsBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/job-positions/merge/bulk`, data);
  }

  getJobPositionReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/job-positions/${id}/references`);
  }

  getJobPositionsPaginated(
    pageNumber: number = 1,
    pageSize: number = 50,
    sortBy: string = 'name',
    ascending: boolean = true
  ): Observable<PaginatedResponse<JobPosition>> {
    const params = {
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
      sortBy,
      ascending: ascending.toString()
    };
    return this.http.get<PaginatedResponse<JobPosition>>(
      `${this.API_BASE_URL}/job-positions`,
      { params }
    );
  }

  searchJobPositions(searchFilter: {
    pageNumber?: number;
    pageSize?: number;
    sortBy?: string;
    ascending?: boolean;
    name?: string;
  }): Observable<PaginatedResponse<JobPosition>> {
    return this.http.post<PaginatedResponse<JobPosition>>(
      `${this.API_BASE_URL}/job-positions/search`,
      searchFilter
    );
  }

  exportJobPositionsToCsv(filter: { name?: string } = {}): Observable<Blob> {
    return this.http.post(`${this.API_BASE_URL}/job-positions/export`, filter, { responseType: 'blob' });
  }

  // ============================================
  // LargeShareholderCategory
  // ============================================
  getLargeShareholderCategories(): Observable<LargeShareholderCategory[]> {
    return this.http.get<LargeShareholderCategory[]>(`${this.API_BASE_URL}/large-shareholder-categories`);
  }

  createLargeShareholderCategory(data: Omit<LargeShareholderCategory, 'id'>): Observable<LargeShareholderCategory> {
    return this.http.post<LargeShareholderCategory>(`${this.API_BASE_URL}/large-shareholder-categories`, data);
  }

  getLargeShareholderCategoryById(id: string): Observable<LargeShareholderCategory> {
    return this.http.get<LargeShareholderCategory>(`${this.API_BASE_URL}/large-shareholder-categories/${id}`);
  }

  updateLargeShareholderCategory(id: string, data: LargeShareholderCategory): Observable<LargeShareholderCategory> {
    return this.http.put<LargeShareholderCategory>(`${this.API_BASE_URL}/large-shareholder-categories/${id}`, data);
  }

  deleteLargeShareholderCategory(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/large-shareholder-categories/${id}`);
  }

  mergeLargeShareholderCategory(leftId: string, rightId: string): Observable<LargeShareholderCategory> {
    return this.http.post<LargeShareholderCategory>(`${this.API_BASE_URL}/large-shareholder-categories/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeLargeShareholderCategoriesBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/large-shareholder-categories/merge/bulk`, data);
  }

  getLargeShareholderCategoryReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/large-shareholder-categories/${id}/references`);
  }

  // ============================================
  // Nationality
  // ============================================
  getNationalities(): Observable<Nationality[]> {
    return this.http.get<Nationality[]>(`${this.API_BASE_URL}/nationalities`);
  }

  createNationality(data: Omit<Nationality, 'id'>): Observable<Nationality> {
    return this.http.post<Nationality>(`${this.API_BASE_URL}/nationalities`, data);
  }

  getNationalityById(id: string): Observable<Nationality> {
    return this.http.get<Nationality>(`${this.API_BASE_URL}/nationalities/${id}`);
  }

  updateNationality(id: string, data: Nationality): Observable<Nationality> {
    return this.http.put<Nationality>(`${this.API_BASE_URL}/nationalities/${id}`, data);
  }

  deleteNationality(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/nationalities/${id}`);
  }

  mergeNationality(leftId: string, rightId: string): Observable<Nationality> {
    return this.http.post<Nationality>(`${this.API_BASE_URL}/nationalities/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeNationalitiesBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/nationalities/merge/bulk`, data);
  }

  getNationalityReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/nationalities/${id}/references`);
  }

  // ============================================
  // PoliticalPosition
  // ============================================
  getPoliticalPositions(): Observable<PoliticalPosition[]> {
    return this.http.get<PoliticalPosition[]>(`${this.API_BASE_URL}/political-positions`);
  }

  createPoliticalPosition(data: Omit<PoliticalPosition, 'id'>): Observable<PoliticalPosition> {
    return this.http.post<PoliticalPosition>(`${this.API_BASE_URL}/political-positions`, data);
  }

  getPoliticalPositionById(id: string): Observable<PoliticalPosition> {
    return this.http.get<PoliticalPosition>(`${this.API_BASE_URL}/political-positions/${id}`);
  }

  updatePoliticalPosition(id: string, data: PoliticalPosition): Observable<PoliticalPosition> {
    return this.http.put<PoliticalPosition>(`${this.API_BASE_URL}/political-positions/${id}`, data);
  }

  deletePoliticalPosition(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/political-positions/${id}`);
  }

  mergePoliticalPosition(leftId: string, rightId: string): Observable<PoliticalPosition> {
    return this.http.post<PoliticalPosition>(`${this.API_BASE_URL}/political-positions/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergePoliticalPositionsBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/political-positions/merge/bulk`, data);
  }

  getPoliticalPositionReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/political-positions/${id}/references`);
  }

  // ============================================
  // Profession
  // ============================================
  getProfessions(): Observable<Profession[]> {
    return this.http.get<Profession[]>(`${this.API_BASE_URL}/professions`);
  }

  createProfession(data: Omit<Profession, 'id'>): Observable<Profession> {
    return this.http.post<Profession>(`${this.API_BASE_URL}/professions`, data);
  }

  getProfessionById(id: string): Observable<Profession> {
    return this.http.get<Profession>(`${this.API_BASE_URL}/professions/${id}`);
  }

  updateProfession(id: string, data: Profession): Observable<Profession> {
    return this.http.put<Profession>(`${this.API_BASE_URL}/professions/${id}`, data);
  }

  deleteProfession(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/professions/${id}`);
  }

  mergeProfession(leftId: string, rightId: string): Observable<Profession> {
    return this.http.post<Profession>(`${this.API_BASE_URL}/professions/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeProfessionsBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/professions/merge/bulk`, data);
  }

  getProfessionReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/professions/${id}/references`);
  }

  // ============================================
  // Qualification
  // ============================================
  getQualifications(): Observable<Qualification[]> {
    return this.http.get<Qualification[]>(`${this.API_BASE_URL}/qualifications`);
  }

  createQualification(data: Omit<Qualification, 'id'>): Observable<Qualification> {
    return this.http.post<Qualification>(`${this.API_BASE_URL}/qualifications`, data);
  }

  getQualificationById(id: string): Observable<Qualification> {
    return this.http.get<Qualification>(`${this.API_BASE_URL}/qualifications/${id}`);
  }

  updateQualification(id: string, data: Qualification): Observable<Qualification> {
    return this.http.put<Qualification>(`${this.API_BASE_URL}/qualifications/${id}`, data);
  }

  deleteQualification(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/qualifications/${id}`);
  }

  mergeQualification(leftId: string, rightId: string): Observable<Qualification> {
    return this.http.post<Qualification>(`${this.API_BASE_URL}/qualifications/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeQualificationsBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/qualifications/merge/bulk`, data);
  }

  getQualificationReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/qualifications/${id}/references`);
  }

  // ============================================
  // StockCategory
  // ============================================
  getStockCategories(): Observable<StockCategory[]> {
    return this.http.get<StockCategory[]>(`${this.API_BASE_URL}/stock-categories`);
  }

  createStockCategory(data: Omit<StockCategory, 'id'>): Observable<StockCategory> {
    return this.http.post<StockCategory>(`${this.API_BASE_URL}/stock-categories`, data);
  }

  getStockCategoryById(id: string): Observable<StockCategory> {
    return this.http.get<StockCategory>(`${this.API_BASE_URL}/stock-categories/${id}`);
  }

  updateStockCategory(id: string, data: StockCategory): Observable<StockCategory> {
    return this.http.put<StockCategory>(`${this.API_BASE_URL}/stock-categories/${id}`, data);
  }

  deleteStockCategory(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/stock-categories/${id}`);
  }

  mergeStockCategory(leftId: string, rightId: string): Observable<StockCategory> {
    return this.http.post<StockCategory>(`${this.API_BASE_URL}/stock-categories/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeStockCategoriesBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/stock-categories/merge/bulk`, data);
  }

  getStockCategoryReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/stock-categories/${id}/references`);
  }

  // ============================================
  // StockCoefficient
  // ============================================
  getStockCoefficients(): Observable<StockCoefficient[]> {
    return this.http.get<StockCoefficient[]>(`${this.API_BASE_URL}/stock-coefficients`);
  }

  createStockCoefficient(data: Omit<StockCoefficient, 'id'>): Observable<StockCoefficient> {
    return this.http.post<StockCoefficient>(`${this.API_BASE_URL}/stock-coefficients`, data);
  }

  getStockCoefficientById(id: string): Observable<StockCoefficient> {
    return this.http.get<StockCoefficient>(`${this.API_BASE_URL}/stock-coefficients/${id}`);
  }

  updateStockCoefficient(id: string, data: StockCoefficient): Observable<StockCoefficient> {
    return this.http.put<StockCoefficient>(`${this.API_BASE_URL}/stock-coefficients/${id}`, data);
  }

  deleteStockCoefficient(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/stock-coefficients/${id}`);
  }

  mergeStockCoefficient(leftId: string, rightId: string): Observable<StockCoefficient> {
    return this.http.post<StockCoefficient>(`${this.API_BASE_URL}/stock-coefficients/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeStockCoefficientsBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/stock-coefficients/merge/bulk`, data);
  }

  getStockCoefficientReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/stock-coefficients/${id}/references`);
  }

  // ============================================
  // StockExchange
  // ============================================
  getStockExchanges(): Observable<StockExchange[]> {
    return this.http.get<StockExchange[]>(`${this.API_BASE_URL}/stock-exchanges`);
  }

  createStockExchange(data: Omit<StockExchange, 'id'>): Observable<StockExchange> {
    return this.http.post<StockExchange>(`${this.API_BASE_URL}/stock-exchanges`, data);
  }

  getStockExchangeById(id: string): Observable<StockExchange> {
    return this.http.get<StockExchange>(`${this.API_BASE_URL}/stock-exchanges/${id}`);
  }

  updateStockExchange(id: string, data: StockExchange): Observable<StockExchange> {
    return this.http.put<StockExchange>(`${this.API_BASE_URL}/stock-exchanges/${id}`, data);
  }

  deleteStockExchange(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/stock-exchanges/${id}`);
  }

  mergeStockExchange(leftId: string, rightId: string): Observable<StockExchange> {
    return this.http.post<StockExchange>(`${this.API_BASE_URL}/stock-exchanges/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeStockExchangesBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/stock-exchanges/merge/bulk`, data);
  }

  getStockExchangeReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/stock-exchanges/${id}/references`);
  }

  // ============================================
  // StockIndex
  // ============================================
  getStockIndices(): Observable<Index[]> {
    return this.http.get<Index[]>(`${this.API_BASE_URL}/stock-indices`);
  }

  createStockIndex(data: Omit<Index, 'id'>): Observable<Index> {
    return this.http.post<Index>(`${this.API_BASE_URL}/stock-indices`, data);
  }

  getStockIndexById(id: string): Observable<Index> {
    return this.http.get<Index>(`${this.API_BASE_URL}/stock-indices/${id}`);
  }

  updateStockIndex(id: string, data: Index): Observable<Index> {
    return this.http.put<Index>(`${this.API_BASE_URL}/stock-indices/${id}`, data);
  }

  deleteStockIndex(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/stock-indices/${id}`);
  }

  mergeStockIndex(leftId: string, rightId: string): Observable<Index> {
    return this.http.post<Index>(`${this.API_BASE_URL}/stock-indices/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeStockIndicesBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/stock-indices/merge/bulk`, data);
  }

  getStockIndexReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/stock-indices/${id}/references`);
  }

  updateCountryAccess(id: string, request: Array<string>): Observable<string[]> {
    return this.http.put<string[]>(`${this.API_BASE_URL}/stock-indices/${id}/countries`, request);
  }

  // ============================================
  // University
  // ============================================
  getUniversities(): Observable<University[]> {
    return this.http.get<University[]>(`${this.API_BASE_URL}/universities`);
  }

  getUniversitiesPaginated(
    pageNumber: number = 1,
    pageSize: number = 50,
    sortBy: string = 'name',
    ascending: boolean = true
  ): Observable<PaginatedResponse<University>> {
    const params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString())
      .set('sortBy', sortBy)
      .set('ascending', ascending.toString());

    return this.http.get<PaginatedResponse<University>>(`${this.API_BASE_URL}/universities`, { params });
  }

  searchUniversities(searchFilter: {
    pageNumber?: number;
    pageSize?: number;
    sortBy?: string;
    ascending?: boolean;
    name?: string;
    countryId?: string;
  }): Observable<PaginatedResponse<University>> {
    return this.http.post<PaginatedResponse<University>>(
      `${this.API_BASE_URL}/universities/search`,
      searchFilter
    );
  }

  createUniversity(data: Omit<University, 'id'>): Observable<University> {
    return this.http.post<University>(`${this.API_BASE_URL}/universities`, data);
  }

  getUniversityById(id: string): Observable<University> {
    return this.http.get<University>(`${this.API_BASE_URL}/universities/${id}`);
  }

  updateUniversity(id: string, data: University): Observable<University> {
    return this.http.put<University>(`${this.API_BASE_URL}/universities/${id}`, data);
  }

  deleteUniversity(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/universities/${id}`);
  }

  mergeUniversity(leftId: string, rightId: string): Observable<University> {
    return this.http.post<University>(`${this.API_BASE_URL}/universities/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeUniversitiesBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/universities/merge/bulk`, data);
  }

  getUniversityReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/universities/${id}/references`);
  }

  exportUniversitiesToCsv(filter: { name?: string; countryId?: string } = {}): Observable<Blob> {
    return this.http.post(`${this.API_BASE_URL}/universities/export`, filter, { responseType: 'blob' });
  }

  // ============================================
  // VoteResultsCategory
  // ============================================
  getVoteResultsCategories(): Observable<VoteResultsCategory[]> {
    return this.http.get<VoteResultsCategory[]>(`${this.API_BASE_URL}/vote-results-categories`);
  }

  createVoteResultsCategory(data: Omit<VoteResultsCategory, 'id'>): Observable<VoteResultsCategory> {
    return this.http.post<VoteResultsCategory>(`${this.API_BASE_URL}/vote-results-categories`, data);
  }

  getVoteResultsCategoryById(id: string): Observable<VoteResultsCategory> {
    return this.http.get<VoteResultsCategory>(`${this.API_BASE_URL}/vote-results-categories/${id}`);
  }

  updateVoteResultsCategory(id: string, data: VoteResultsCategory): Observable<VoteResultsCategory> {
    return this.http.put<VoteResultsCategory>(`${this.API_BASE_URL}/vote-results-categories/${id}`, data);
  }

  deleteVoteResultsCategory(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/vote-results-categories/${id}`);
  }

  mergeVoteResultsCategory(leftId: string, rightId: string): Observable<VoteResultsCategory> {
    return this.http.post<VoteResultsCategory>(`${this.API_BASE_URL}/vote-results-categories/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeVoteResultsCategoriesBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/vote-results-categories/merge/bulk`, data);
  }

  getVoteResultsCategoryReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/vote-results-categories/${id}/references`);
  }

  // ============================================
  // VoteResultsOption
  // ============================================
  getVoteResultsOptions(): Observable<VoteResultsOption[]> {
    return this.http.get<VoteResultsOption[]>(`${this.API_BASE_URL}/vote-results-options`);
  }

  createVoteResultsOption(data: Omit<VoteResultsOption, 'id'>): Observable<VoteResultsOption> {
    return this.http.post<VoteResultsOption>(`${this.API_BASE_URL}/vote-results-options`, data);
  }

  getVoteResultsOptionById(id: string): Observable<VoteResultsOption> {
    return this.http.get<VoteResultsOption>(`${this.API_BASE_URL}/vote-results-options/${id}`);
  }

  updateVoteResultsOption(id: string, data: VoteResultsOption): Observable<VoteResultsOption> {
    return this.http.put<VoteResultsOption>(`${this.API_BASE_URL}/vote-results-options/${id}`, data);
  }

  deleteVoteResultsOption(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/vote-results-options/${id}`);
  }

  mergeVoteResultsOption(leftId: string, rightId: string): Observable<VoteResultsOption> {
    return this.http.post<VoteResultsOption>(`${this.API_BASE_URL}/vote-results-options/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeVoteResultsOptionsBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/vote-results-options/merge/bulk`, data);
  }

  getVoteResultsOptionReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/vote-results-options/${id}/references`);
  }

  // ============================================
  // WorkExperienceCompany
  // ============================================
  getWorkExperienceCompanies(): Observable<WorkExperienceCompany[]> {
    return this.http.get<WorkExperienceCompany[]>(`${this.API_BASE_URL}/work-experience-companies`);
  }

  getWorkExperienceCompaniesPaginated(
    pageNumber: number = 1,
    pageSize: number = 50,
    sortBy: string = 'name',
    ascending: boolean = true
  ): Observable<PaginatedResponse<WorkExperienceCompany>> {
    const params = {
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
      sortBy,
      ascending: ascending.toString()
    };
    return this.http.get<PaginatedResponse<WorkExperienceCompany>>(
      `${this.API_BASE_URL}/work-experience-companies`,
      { params }
    );
  }

  searchWorkExperienceCompanies(searchFilter: {
    pageNumber?: number;
    pageSize?: number;
    sortBy?: string;
    ascending?: boolean;
    name?: string;
    countryId?: string;
  }): Observable<PaginatedResponse<WorkExperienceCompany>> {
    return this.http.post<PaginatedResponse<WorkExperienceCompany>>(
      `${this.API_BASE_URL}/work-experience-companies/search`,
      searchFilter
    );
  }

  createWorkExperienceCompany(data: Omit<WorkExperienceCompany, 'id'>): Observable<WorkExperienceCompany> {
    return this.http.post<WorkExperienceCompany>(`${this.API_BASE_URL}/work-experience-companies`, data);
  }

  getWorkExperienceCompanyById(id: string): Observable<WorkExperienceCompany> {
    return this.http.get<WorkExperienceCompany>(`${this.API_BASE_URL}/work-experience-companies/${id}`);
  }

  updateWorkExperienceCompany(id: string, data: WorkExperienceCompany): Observable<WorkExperienceCompany> {
    return this.http.put<WorkExperienceCompany>(`${this.API_BASE_URL}/work-experience-companies/${id}`, data);
  }

  deleteWorkExperienceCompany(id: string): Observable<void> {
    return this.http.delete<void>(`${this.API_BASE_URL}/work-experience-companies/${id}`);
  }

  mergeWorkExperienceCompany(leftId: string, rightId: string): Observable<WorkExperienceCompany> {
    return this.http.post<WorkExperienceCompany>(`${this.API_BASE_URL}/work-experience-companies/merge?leftId=${leftId}&rightId=${rightId}`, null);
  }

  mergeWorkExperienceCompaniesBulk(data: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}/work-experience-companies/merge/bulk`, data);
  }

  getWorkExperienceCompanyReferences(id: string): Observable<unknown> {
    return this.http.get<unknown>(`${this.API_BASE_URL}/work-experience-companies/${id}/references`);
  }

  exportWorkExperienceCompaniesToCsv(filter: { name?: string; countryId?: string } = {}): Observable<Blob> {
    return this.http.post(`${this.API_BASE_URL}/work-experience-companies/export`, filter, { responseType: 'blob' });
  }
}
